import {
    Alert,
    Box,
    Button,
    Checkbox,
    FormControl,
    FormControlLabel,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableRow,
    TextField,
    Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import weekday from "dayjs/plugin/weekday";
import React, {
    forwardRef,
    useCallback,
    useEffect,
    useImperativeHandle,
    useLayoutEffect,
    useRef,
    useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { getFolders } from "../api/folder";
import {
    createChecklist,
    createTodos,
    getTemplates,
} from "../api/selectTemplate";

import FeedbackSnackbar from "../components/FeedbackSnackbar";
import Loading from "../components/Loading";
import TemplateListHeader from "../components/TemplateListHeader";

import { useConfig } from "../contexts/ConfigContext";

/** AppShell の永続ドロワー幅（`AppShell.js` の DRAWER_WIDTH と一致） */
const MAIN_DRAWER_WIDTH_PX = 280;

const utc = require("dayjs/plugin/utc");
const timezone = require("dayjs/plugin/timezone");

dayjs.extend(weekday);
dayjs.extend(utc);
dayjs.extend(timezone);

function getScheduleDates(
  startDate,
  endDate,
  repeatOption,
  notify,
  tz = "Asia/Tokyo"
) {
  if (!startDate && !endDate && !repeatOption) {
    // 開始日、終了日、繰り返しオプションが設定されていない場合、現在の日付を配列で返す
    return [dayjs().tz(tz).format()];
  }

  if (!startDate || !endDate || !repeatOption) {
    notify(
      "繰り返しの開始日、終了日、繰り返しオプションを設定してください。\n" +
        "または、全て設定しなければ現在日時で作成されます。",
      "warning"
    );
    return;
  }

  let dates = [];
  let currentDate = dayjs(startDate).tz(tz); // JSTを設定する
  const endDateDayjs = dayjs(endDate).tz(tz);

  if (currentDate.isAfter(endDateDayjs)) {
    notify("終了日が開始日より前になっています。", "warning");
    return;
  }

  while (
    currentDate.isBefore(endDateDayjs) ||
    currentDate.isSame(endDateDayjs)
  ) {
    dates.push(currentDate.format()); // ISO 8601形式のタイムゾーン付きタイムスタンプを追加
    switch (repeatOption) {
      case "daily":
        currentDate = currentDate.add(1, "day");
        break;
      case "weekly":
        currentDate = currentDate.add(1, "week");
        break;
      case "monthly":
        currentDate = currentDate.add(1, "month");
        break;
      default:
        // 繰り返しオプションが設定されていない場合、現在の日付を配列で返す
        // ここには来ないはずなのでなんでもいい
        return [dayjs().tz(tz).format()];
    }
  }

  return dates;
}

const ScheduleTasks = forwardRef(function ScheduleTasks(
  { selectedTemplates, loading, setLoading, showMessage, embedded = false },
  ref
) {
  const [repeatOption, setRepeatOption] = useState("");
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const navigate = useNavigate();

  const handleStartDateChange = (newValue) => {
    setStartDate(newValue);
  };

  const handleEndDateChange = (newValue) => {
    setEndDate(newValue);
  };

  const handleRepeatChange = (event) => {
    setRepeatOption(event.target.value);
  };

  const createTodoList = useCallback(() => {
    const selectedIds = Object.keys(selectedTemplates).filter(
      (id) => selectedTemplates[id]
    );

    if (!selectedIds.length) {
      showMessage("テンプレートを選択してください。", "warning");
      return;
    }

    const dates = getScheduleDates(
      startDate,
      endDate,
      repeatOption,
      showMessage
    );

    if (!dates) {
      return;
    }
    setLoading(true);
    postSelectedTemplates(selectedIds, dates)
      .then(() => {
        setLoading(false);
        showMessage("ワースケを作成しました。", "success", (
          <Button color="inherit" size="small" onClick={() => navigate("/")}>
            ホームへ
          </Button>
        ));
      })
      .catch((error) => {
        console.error("ワースケ作成中にエラーが発生しました:", error);
        showMessage("ワースケ作成中にエラーが発生しました。", "error");
        setLoading(false);
      });
  }, [
    selectedTemplates,
    startDate,
    endDate,
    repeatOption,
    setLoading,
    showMessage,
    navigate,
  ]);

  useImperativeHandle(ref, () => ({ submit: createTodoList }), [createTodoList]);

  if (loading) {
    return <Loading />;
  }

  const compact = embedded;
  const form = (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Stack spacing={compact ? 1 : 1.5}>
        <Typography variant="subtitle2" sx={{ lineHeight: 1.3 }}>
          繰り返しオプション（省略可能）
        </Typography>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" },
            gap: compact ? 1 : 1.5,
          }}
        >
          <DatePicker
            label="開始日"
            value={startDate}
            onChange={handleStartDateChange}
            slotProps={{ textField: { size: "small" } }}
          />
          <DatePicker
            label="終了日"
            value={endDate}
            onChange={handleEndDateChange}
            slotProps={{ textField: { size: "small" } }}
          />
        </Box>
        <FormControl fullWidth size="small">
          <InputLabel id="repeat-select-label">繰り返し</InputLabel>
          <Select
            labelId="repeat-select-label"
            id="repeat-select"
            value={repeatOption}
            label="繰り返し"
            onChange={handleRepeatChange}
          >
            <MenuItem value="daily">毎日</MenuItem>
            <MenuItem value="weekly">毎週</MenuItem>
            <MenuItem value="monthly">毎月</MenuItem>
          </Select>
        </FormControl>
      </Stack>
    </LocalizationProvider>
  );

  if (embedded) {
    return <Box sx={{ width: "100%" }}>{form}</Box>;
  }

  return <Paper sx={{ p: 2 }}>{form}</Paper>;
});

const ChecklistCreationForm = forwardRef(function ChecklistCreationForm(
  { selectedTemplates, loading, setLoading, showMessage, embedded = false },
  ref
) {
  const [assignee, setAssignee] = useState("");
  const [folders, setFolders] = useState([]);
  const [selectedFolder, setSelectedFolder] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    async function fetchFolders() {
      try {
        const folderData = await getFolders();
        setFolders(folderData);
      } catch (error) {
        console.error("Failed to fetch folders:", error);
      }
    }
    fetchFolders();
  }, []);

  const createChecklistFromTemplate = useCallback(async () => {
    const selectedIds = Object.keys(selectedTemplates).filter(
      (id) => selectedTemplates[id]
    );

    if (!selectedIds.length) {
      showMessage("テンプレートを選択してください。", "warning");
      return;
    }

    setLoading(true);

    try {
      for (const id of selectedIds) {
        await createChecklist({
          template_id: id,
          assignee: assignee,
          folder_id: selectedFolder || null,
        });
      }
      setLoading(false);
      showMessage("チェックリストを作成しました。", "success", (
        <Button color="inherit" size="small" onClick={() => navigate("/")}>
          ホームへ
        </Button>
      ));
    } catch (error) {
      console.error("チェックリスト作成中にエラーが発生しました:", error);
      showMessage("チェックリスト作成中にエラーが発生しました。", "error");
      setLoading(false);
    }
  }, [
    selectedTemplates,
    assignee,
    selectedFolder,
    setLoading,
    showMessage,
    navigate,
  ]);

  useImperativeHandle(
    ref,
    () => ({ submit: createChecklistFromTemplate }),
    [createChecklistFromTemplate]
  );

  if (loading) {
    return <Loading />;
  }

  const compact = embedded;
  const form = (
    <Stack spacing={compact ? 1 : 1.5}>
      <Typography variant="subtitle2" sx={{ lineHeight: 1.3 }}>
        チェックリスト作成
      </Typography>
      <TextField
        label="担当者"
        size="small"
        value={assignee}
        onChange={(e) => setAssignee(e.target.value)}
      />
      <FormControl fullWidth size="small">
        <InputLabel id="folder-select-label">フォルダ（任意）</InputLabel>
        <Select
          labelId="folder-select-label"
          value={selectedFolder}
          onChange={(e) => setSelectedFolder(e.target.value)}
          label="フォルダ（任意）"
        >
          <MenuItem value="">未選択</MenuItem>
          {folders.map((folder) => (
            <MenuItem key={folder.id} value={folder.id}>
              {folder.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Stack>
  );

  if (embedded) {
    return <Box sx={{ width: "100%" }}>{form}</Box>;
  }

  return <Paper sx={{ p: 2 }}>{form}</Paper>;
});

function SelectTemplate() {
  const location = useLocation();
  const [templates, setTemplates] = useState([]);
  const [templates_display, setTemplatesDisplay] = useState([]);
  const [selectedTemplates, setSelectedTemplates] = useState({});
  const [loading, setLoading] = useState(true);
  const [creationMode, setCreationMode] = useState(() => {
    const params = new URLSearchParams(location.search);
    return params.get("creationMode") === "checklist" ? "checklist" : "todo";
  });
  const [feedback, setFeedback] = useState({
    open: false,
    message: "",
    severity: "success",
    action: null,
  });

  const scheduleTasksRef = useRef(null);
  const checklistFormRef = useRef(null);
  const creationFooterRef = useRef(null);
  const [creationFooterPad, setCreationFooterPad] = useState(260);

  const showSnackbar = (message, severity = "success", action = null) => {
    setFeedback({ open: true, message, severity, action });
  };

  const loadTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getTemplates();
      const response_sorted = response.sort((a, b) =>
        a.name.localeCompare(b.name)
      );
      setTemplates(response_sorted);
      setTemplatesDisplay(response_sorted);
    } catch (error) {
      console.error("テンプレートの取得時にエラーが発生しました:", error);
      showSnackbar("テンプレートの読み込みに失敗しました。", "error");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  const handleFilterChange = useCallback(
    (filteredTemplates) => {
      setTemplatesDisplay(filteredTemplates);
      const currentSelected = { ...selectedTemplates };
      const displayedIds = new Set(filteredTemplates.map((t) => t.id));
      let changed = false;
      Object.keys(currentSelected).forEach((idStr) => {
        const id = parseInt(idStr, 10);
        if (currentSelected[id] && !displayedIds.has(id)) {
          delete currentSelected[id];
          changed = true;
        }
      });
      if (changed) {
        setSelectedTemplates(currentSelected);
      }
    },
    [selectedTemplates]
  );

  const handleCheckboxChange = (id) => {
    setSelectedTemplates((prevSelected) => ({
      ...prevSelected,
      [id]: !prevSelected[id],
    }));
  };

  const handleSnackbarClose = () => {
    setFeedback((prev) => ({ ...prev, open: false }));
  };

  const config = useConfig();
  const tagSelectorFeatureEnabled = config?.features?.enableTagSelector ?? false;
  const checkListEnabled = Boolean(config?.features?.enableCheckList);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const nextMode =
      params.get("creationMode") === "checklist" ? "checklist" : "todo";
    setCreationMode(checkListEnabled && nextMode === "checklist" ? "checklist" : "todo");
  }, [location.search, checkListEnabled]);

  useLayoutEffect(() => {
    const el = creationFooterRef.current;
    if (!el) {
      return undefined;
    }
    const measure = () => {
      setCreationFooterPad(Math.ceil(el.getBoundingClientRect().height) + 12);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => {
      ro.disconnect();
    };
  }, [creationMode, checkListEnabled]);

  if (loading) {
    return <Loading />;
  }

  const handleFooterPrimary = () => {
    if (creationMode === "todo") {
      scheduleTasksRef.current?.submit();
    } else {
      checklistFormRef.current?.submit();
    }
  };

  const footerPrimaryLabel =
    creationMode === "todo" ? "ワースケを作成する" : "チェックリストを作成する";

  return (
    <>
      <Box
        sx={{
          pb: `${creationFooterPad}px`,
        }}
      >
        <Stack spacing={2}>
          <TemplateListHeader
            templates={templates}
            onFilterChange={handleFilterChange}
            tagSelectorEnabled={tagSelectorFeatureEnabled}
          />

          <Paper sx={{ p: 1.5 }}>
            {templates.length === 0 ? (
              <Alert severity="info">
                テンプレートがありません。テンプレート画面から作成してください。
              </Alert>
            ) : templates_display.length === 0 ? (
              <Alert severity="info">条件に一致するテンプレートはありません。</Alert>
            ) : (
              <TableContainer>
                <Table>
                  <TableBody>
                    {templates_display.map((template) => (
                      <TableRow key={template.id}>
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={selectedTemplates[template.id] || false}
                            onChange={() => handleCheckboxChange(template.id)}
                          />
                        </TableCell>
                        <TableCell>{template.name}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        </Stack>
      </Box>

      <Paper
        ref={creationFooterRef}
        component="footer"
        elevation={0}
        square
        sx={{
          position: "fixed",
          bottom: 0,
          right: 0,
          left: { xs: 0, lg: `${MAIN_DRAWER_WIDTH_PX}px` },
          zIndex: (theme) => theme.zIndex.appBar - 1,
          px: { xs: 1.5, sm: 2, lg: 3 },
          pt: 1,
          pb: {
            xs: "calc(8px + env(safe-area-inset-bottom, 0px))",
            sm: "calc(8px + env(safe-area-inset-bottom, 0px))",
          },
          borderTop: 1,
          borderColor: "divider",
          backgroundColor: (theme) => alpha(theme.palette.background.paper, 0.92),
          backdropFilter: "blur(14px)",
          maxHeight: { xs: "min(58vh, 420px)", sm: "min(52vh, 400px)" },
          overflowY: "auto",
        }}
      >
        <Stack
          spacing={1}
          sx={{
            maxWidth: { lg: 1200 },
            mx: { lg: "auto" },
          }}
        >
          {creationMode === "todo" ? (
            <ScheduleTasks
              ref={scheduleTasksRef}
              embedded
              selectedTemplates={selectedTemplates}
              loading={loading}
              setLoading={setLoading}
              showMessage={showSnackbar}
            />
          ) : (
            <ChecklistCreationForm
              ref={checklistFormRef}
              embedded
              selectedTemplates={selectedTemplates}
              loading={loading}
              setLoading={setLoading}
              showMessage={showSnackbar}
            />
          )}

          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              alignItems: { xs: "stretch", sm: "center" },
              justifyContent: checkListEnabled ? "space-between" : "flex-end",
              gap: { xs: 1, sm: 1.5 },
              mt: 0.5,
              pt: 1,
              borderTop: 1,
              borderColor: "divider",
            }}
          >
            {checkListEnabled && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={creationMode === "checklist"}
                    onChange={(e) =>
                      setCreationMode(e.target.checked ? "checklist" : "todo")
                    }
                    disabled={!checkListEnabled}
                    size="small"
                  />
                }
                label="チェックリストとして作成"
                sx={{
                  m: 0,
                  alignItems: "center",
                  "& .MuiFormControlLabel-label": {
                    fontSize: "0.875rem",
                    fontWeight: 500,
                    color: "text.primary",
                  },
                }}
              />
            )}
            <Button
              variant="contained"
              size="medium"
              onClick={handleFooterPrimary}
              sx={{
                flexShrink: 0,
                width: { xs: "100%", sm: "auto" },
                minWidth: { sm: 180 },
                alignSelf: { xs: "stretch", sm: "center" },
              }}
            >
              {footerPrimaryLabel}
            </Button>
          </Box>
        </Stack>
      </Paper>

      <FeedbackSnackbar
        open={feedback.open}
        message={feedback.message}
        severity={feedback.severity}
        action={feedback.action}
        onClose={handleSnackbarClose}
      />
    </>
  );
}

export default SelectTemplate;

async function postSelectedTemplates(selectedIds, scheduledDates) {
  const results = [];
  for (const date of scheduledDates) {
    for (const id of selectedIds) {
      await createTodos({
        template_id: id,
        scheduled_on: dayjs(date).tz("Asia/Tokyo").format("YYYY-MM-DD"),
      })
        .then((response) => {
          results.push(response);
        })
        .catch((error) => {
          console.error("ワースケ作成中にエラーが発生しました:", error);
        });
    }
  }

  return results;
}
